/* Custom gallery for products whose variant images come from the
   custom.variant_image metafield instead of product media.

   Swiper (vendored: sqz-swiper.min.*) drives a main slider and a synced
   thumbnail strip; a native <dialog> provides the lightbox. The LAST
   slide is the selected variant's render: the gallery opens on the
   editorial media (images and videos), and Dawn's variantChange event
   swaps the render in place and slides to it.

   Deliberately not a media-gallery element — Dawn's setActiveMedia() only
   understands product media and fights over slide visibility. */

class SqzGallery extends HTMLElement {
  connectedCallback() {
    if (typeof Swiper === 'undefined') {
      // In quick-add / combined-listing contexts, HTMLUpdateUtility re-injects
      // script tags and defer order is not preserved — this 3 KB file can run
      // before the 154 KB Swiper bundle. The load event fires after execution,
      // so retrying on it is guaranteed to see the global.
      var swiperScript = document.querySelector('script[src*="sqz-swiper.min.js"]');
      if (swiperScript) {
        var self = this;
        swiperScript.addEventListener(
          'load',
          function () {
            if (self.isConnected) self.connectedCallback();
          },
          { once: true }
        );
      } else {
        console.warn('sqz-gallery: Swiper not loaded');
      }
      return;
    }
    if (this.mainSwiper) return; // re-connected without a disconnect

    this.renderImg = this.querySelector('[data-sqz-render-img]');
    this.renderThumb = this.querySelector('[data-sqz-render-thumb]');
    this.frame = this.querySelector('.sqz-gallery__frame');
    this.lightbox = this.querySelector('[data-sqz-lightbox]');
    this.lightboxImg = this.querySelector('[data-sqz-lightbox-img]');
    // Both are null on products where the initial variant has no imagery —
    // the gallery is then editorial-only (or just the placeholder).
    this.renderSizes = this.renderImg ? this.renderImg.getAttribute('sizes') || '' : '';
    this.speed = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 500;

    this.urls = {};
    try {
      this.urls = JSON.parse(this.querySelector('[data-sqz-render-map]').textContent);
    } catch (e) {
      /* map stays empty; the gallery still works, minus variant swaps */
    }

    this.thumbsSwiper = new Swiper(this.querySelector('[data-sqz-thumbs]'), {
      slidesPerView: 'auto',
      spaceBetween: 8,
      freeMode: true,
      watchSlidesProgress: true,
    });

    var self = this;
    this.mainSwiper = new Swiper(this.querySelector('[data-sqz-main]'), {
      speed: this.speed,
      spaceBetween: 24,
      navigation: {
        prevEl: this.querySelector('.sqz-gallery__frame .sqz-gallery__nav--prev'),
        nextEl: this.querySelector('.sqz-gallery__frame .sqz-gallery__nav--next'),
      },
      thumbs: { swiper: this.thumbsSwiper },
      on: {
        slideChange: function () {
          self.onSlideChange();
        },
      },
    });
    this.onSlideChange();

    this.initLightbox();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.onVariantChange.bind(this)
      );
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    if (this.mainSwiper) this.mainSwiper.destroy(true, false);
    if (this.thumbsSwiper) this.thumbsSwiper.destroy(true, false);
    this.mainSwiper = this.thumbsSwiper = null;
  }

  /* -- slide changes ------------------------------------------------------ */

  onSlideChange() {
    if (!this.mainSwiper) return;
    var active = this.mainSwiper.slides[this.mainSwiper.activeIndex];

    // A video keeps playing behind the next slide otherwise. But entering
    // fullscreen resizes the viewport, Swiper recalcs and fires slide
    // events, and this handler would pause the very video the customer
    // just expanded — so anything fullscreen is off limits.
    var fs = document.fullscreenElement || document.webkitFullscreenElement;
    this.querySelectorAll('[data-sqz-main] video').forEach(function (v) {
      if (active && active.contains(v)) return;
      if (fs && (fs === v || fs.contains(v))) return;
      if (v.webkitDisplayingFullscreen) return; // iOS's own fullscreen flag
      v.pause();
    });

    // The lightbox is images-only, so the zoom control leaves with them.
    // Direct child only: video_tag ships a fallback <img> INSIDE <video>,
    // which must not count as the slide being an image.
    var zoom = this.querySelector('[data-sqz-zoom]');
    if (zoom) zoom.hidden = !(active && active.querySelector(':scope > img'));
  }

  /* -- loading state ------------------------------------------------------ */

  trackLoading(img) {
    var frame = this.frame;
    frame.classList.add('is-loading');
    var done = function () {
      frame.classList.remove('is-loading');
    };
    if (img.complete && img.naturalWidth) return done();
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
  }

  /* -- variant changes ---------------------------------------------------- */

  srcsetFor(fullUrl) {
    // Shopify CDN URLs carry ?width=1946; deriving the smaller candidates
    // keeps responsive loading alive after the initial srcset is replaced.
    if (fullUrl.indexOf('width=1946') === -1) return '';
    return [713, 990, 1426, 1946]
      .map(function (w) {
        return fullUrl.replace('width=1946', 'width=' + w) + ' ' + w + 'w';
      })
      .join(', ');
  }

  onVariantChange(event) {
    var data = event && event.data;
    var variant = data && data.variant;
    if (!variant) return;

    // Every product-info on the page publishes on the same bus — quick-add
    // modals and featured-product sections included. Only the host product's
    // own changes may touch this gallery (and, critically, the reload
    // fallback below). Section ids collide for products sharing a template,
    // hence the second check against the publisher's product id.
    if (data.sectionId && data.sectionId !== this.dataset.sectionId) return;
    var publisher = data.html && data.html.querySelector('product-info');
    if (publisher && publisher.dataset.productId !== this.dataset.productId) return;

    // Map semantics: an object means the variant has an image to show; null
    // means Liquid saw the variant and it has no imagery (leave the gallery
    // alone); ABSENT means Liquid never saw it — past the 250-variant cap.
    var known = Object.prototype.hasOwnProperty.call(this.urls, variant.id);
    var entry = this.urls[variant.id];

    if (entry === null) return;

    if (!known || !this.renderImg) {
      // Past the cap, or the page rendered without a variant slide because
      // the initial variant had no imagery. Either way the server can render
      // this variant correctly — reload with it in the URL. Unconditional:
      // Dawn's updateURL() has already written ?variant= via replaceState
      // before this event fires, so comparing against the URL would always
      // conclude there is nothing to do. No loop risk — this event only
      // fires from user interaction, never on page load.
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.location.assign(url.toString());
      return;
    }

    this.renderImg.srcset = this.srcsetFor(entry.full);
    this.renderImg.sizes = this.renderSizes;
    this.renderImg.src = entry.full;
    if (entry.title) this.renderImg.alt = entry.title;
    this.renderThumb.src = entry.thumb;
    this.trackLoading(this.renderImg);

    // Selecting a variant means wanting to see it — its render is the
    // last slide, wherever that lands once videos join the deck.
    if (this.mainSwiper) {
      var slide = this.renderImg.closest('.swiper-slide');
      var index = this.mainSwiper.slides.indexOf(slide);
      this.mainSwiper.slideTo(index === -1 ? this.mainSwiper.slides.length - 1 : index, this.speed);
    }
  }

  /* -- lightbox ----------------------------------------------------------- */

  /* The lightbox is images-only, but video slides sit between images, so
     image order and slide order no longer agree. Each entry carries its
     slide index so stepping the lightbox can keep the gallery in step. */
  imageEntries() {
    var slides = Array.prototype.slice.call(
      this.querySelectorAll('[data-sqz-main] .sqz-gallery__slide')
    );
    var entries = [];
    slides.forEach(function (slide, i) {
      // Direct child only — video slides carry a fallback <img> inside
      // the <video>, and posters don't belong in the lightbox.
      var img = slide.querySelector(':scope > img');
      if (img) entries.push({ img: img, slideIndex: i });
    });
    return entries;
  }

  openLightbox(slideIndex) {
    var entries = this.imageEntries();
    var index = -1;
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].slideIndex === slideIndex) index = i;
    }
    if (index === -1) return; // a video slide — zoom is hidden there anyway
    this.lightboxIndex = index;
    this.showLightboxImage();
    this.lightbox.showModal();
  }

  showLightboxImage() {
    var entry = this.imageEntries()[this.lightboxIndex];
    if (!entry) return;
    this.lightboxImg.src = entry.img.getAttribute('src');
    this.lightboxImg.alt = entry.img.alt || '';
    // Keep the main gallery (and thumbs) on the same image.
    if (this.mainSwiper) this.mainSwiper.slideTo(entry.slideIndex, this.speed);
  }

  stepLightbox(delta) {
    var count = this.imageEntries().length;
    if (!count) return;
    this.lightboxIndex = (this.lightboxIndex + delta + count) % count;
    this.showLightboxImage();
  }

  initLightbox() {
    var self = this;

    this.querySelector('[data-sqz-zoom]').addEventListener('click', function () {
      self.openLightbox(self.mainSwiper ? self.mainSwiper.activeIndex : 0);
    });

    this.querySelector('[data-sqz-lightbox-close]').addEventListener('click', function () {
      self.lightbox.close();
    });
    this.querySelector('[data-sqz-lightbox-prev]').addEventListener('click', function () {
      self.stepLightbox(-1);
    });
    this.querySelector('[data-sqz-lightbox-next]').addEventListener('click', function () {
      self.stepLightbox(1);
    });

    // Backdrop click closes: the dialog itself is the target only when the
    // click lands outside its laid-out children.
    this.lightbox.addEventListener('click', function (e) {
      if (e.target === self.lightbox) self.lightbox.close();
    });

    this.lightbox.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') self.stepLightbox(-1);
      if (e.key === 'ArrowRight') self.stepLightbox(1);
    });
  }
}

if (!customElements.get('sqz-gallery')) {
  customElements.define('sqz-gallery', SqzGallery);
}
